import { defineStore } from "pinia";
import { ref } from "vue";
import { INSPECTION_LINKS_ENDPOINT } from "@/constants/Endpoints";
import axios from "axios";
import { LOGGER } from "@/util/logger";
import { clone } from "@/util/clone";
import { MODULE_TYPES } from "@/constants/InspectionConstants";
import { sortStructure } from "@/util/sortStructure.js";

export const useDeckStore = defineStore("deck", () => {
  //deck data is loaded either from idb(pouch db for inspection module) or service(for management module)
  let deckData = ref({});
  let deckDataOriginal = ref({});

  const loadDeckObject = async (moduleType, brkey, inspectionId, structure) => {
    let isSuccess = false;
    if (moduleType === MODULE_TYPES.INSPECTION) {
      //if moduleType is inspection, load data from pouchDB
      convertToInspModuleObject(structure);
      isSuccess = true;
    } else if (moduleType === MODULE_TYPES.MANAGEMENT) {
      //if moduleType is management, load data from service call
      isSuccess = await getInspLinkDeck(brkey, inspectionId);
    }
    return isSuccess;
  };

  const getDeckObject = () => {
    return deckData.value;
  };

  const resetPageData = () => {
    deckData.value = clone(deckDataOriginal.value);
  };

  const saveDeck = async (moduleType, deckValue, structure) => {
    let isSuccess = false;
    if (moduleType === MODULE_TYPES.INSPECTION) {
      convertToSaveInspModuleObject(deckValue, structure);
      isSuccess = true;
    } else if (moduleType === MODULE_TYPES.MANAGEMENT) {
      isSuccess = await updateDeck(convertToSaveInspLinkObject(deckValue));
    }
    return isSuccess;
  };

  const updateDeck = async (deck) => {
    let isSuccess = true;
    const url = INSPECTION_LINKS_ENDPOINT.UPDATE_DECK;
    await axios
      .put(url, deck)
      .then((response) => {
        LOGGER.logEvent(LOGGER.EventAction.Update, LOGGER.EventCat.RequestResponse, "Update Deck", response);
        convertToInspLinkObject(response.data.data);
      })
      .catch((e) => {
        LOGGER.logException(e);
        isSuccess = false;
      });
    return isSuccess;
  };

  const getTotalNumberOfSpans = (structure) => {
    const deckSpanConfigs = new Set(["A", "M"]);
    return structure?.structureUnitSpanSets?.reduce((total, spanSet) => {
      if (deckSpanConfigs.has(spanSet.spanConfigDesignation?.[0])) {
        return total + spanSet?.numberOfSpans || 0;
      }
      return total;
    }, 0);
  };

  const getStructureUnits = (structure) => {
    const sortedStructures = sortStructure(clone(structure), "structureUnits", "strUnitLabel");
    return sortedStructures?.filter((structureUnit) => ["A", "M"].includes(structureUnit.legacyStructureType));
  };

  const convertToSaveInspLinkObject = (deck) => {
    return {
      brkey: deck.brkey,
      inspectionId: deck.inspectionId,
      dkRatingDuration: deck.dkRatingDuration,
      dkRating: deck.dkRating,
      deckWsSurfDuration: deck.deckWsCondRateDuration,
      deckWsCondRate: deck.deckWsCondRate,
      dkSurfType: deck.dkSurfType,
      dkMembType: deck.dkMembType,
      dkProtect: deck.dkProtect,
      mainWsThickness: deck.mainWsThickness,
      apprWsThickness: deck.apprWsThickness,
      mainWsThickDate: deck.mainWsThickDate,
      apprWsThickDate: deck.apprWsThickDate,
      protectYear: deck.protectYear,
      protectNote: deck.protectNote,
      deckCrackingMetric: deck.deckCrackingMetric,
      maxStructureHeight: deck.maxStructureHeight,
      dkStrucType: deck.dkStrucType,
      deckWidth: deck.deckWidth,
      lftCurbSw: deck.lftCurbSw,
      rtCurbSw: deck.rtCurbSw,
      bridgeMed: deck.bridgeMed,
      roadWidth: deck.roadWidth,
      structureUnits: deck.structureUnits,
      inspectionComments: deck.inspectionComments,
      structureUnitInspComments: deck.structureUnitInspComments,
      nbisBridgeLength: deck.nbisBridgeLength,
      estChlorideContentPct: deck.estChlorideContentPct,
      estChlorideContentDate: deck.estChlorideContentDate,
      wearingSurfaceThickness: deck.wearingSurfaceThickness,
      wearingSurfaceThicknessDate: deck.wearingSurfaceThicknessDate,
      wearingSurfaceThicknessOver: deck.wearingSurfaceThicknessOver,
      wearingSurfaceThicknessUnder: deck.wearingSurfaceThicknessUnder,
      totalNumberOfSpans: deck.totalNumberOfSpans,
    };
  };

  const convertToSaveInspModuleObject = (deckValue, structure) => {
    structure.inspectionConditionHist.dkRatingDuration = deckValue.dkRatingDuration;
    structure.inspectionConditionHist.dkRatingDurationChanges = deckValue.dkRatingDurationChanges;
    structure.inspection.dkRating = deckValue.dkRating;
    structure.inspectionConditionHist.deckWsCondRateDuration = deckValue.deckWsCondRateDuration;
    structure.inspectionConditionHist.deckWsCondRateDurationChanges = deckValue.deckWsCondRateDurationChanges;
    structure.userInspection.deckWsCondRate = deckValue.deckWsCondRate;
    structure.structureBridge.dkSurfType = deckValue.dkSurfType;
    structure.structureBridge.dkMembType = deckValue.dkMembType;
    structure.structureBridge.dkProtect = deckValue.dkProtect;
    structure.userInspection.mainWsThickness = deckValue.mainWsThickness;
    structure.userInspection.apprWsThickness = deckValue.apprWsThickness;
    structure.userInspection.mainWsThickDate = deckValue.mainWsThickDate;
    structure.userInspection.apprWsThickDate = deckValue.apprWsThickDate;
    structure.userInspection.estChlorideContentPct = deckValue.estChlorideContentPct;
    structure.userInspection.estChlorideContentDate = deckValue.estChlorideContentDate;
    structure.userInspection.wearingSurfaceThickness = deckValue.wearingSurfaceThickness;
    structure.userInspection.wearingSurfaceThicknessDate = deckValue.wearingSurfaceThicknessDate;
    structure.userInspection.wearingSurfaceThicknessOver = deckValue.wearingSurfaceThicknessOver;
    structure.userInspection.wearingSurfaceThicknessUnder = deckValue.wearingSurfaceThicknessUnder;
    structure.userBridge.protectYear = deckValue.protectYear;
    structure.userBridge.protectNote = deckValue.protectNote;
    structure.userInspection.deckCrackingMetric = deckValue.deckCrackingMetric;
    structure.userBridge.maxStructureHeight = deckValue.maxStructureHeight;
    structure.structureBridge.dkStrucType = deckValue.dkStrucType;
    structure.structureBridge.nbisBridgeLength = deckValue.nbisBridgeLength;
    structure.structureUnitInspComments = deckValue.structureUnitInspComments;
    structure.structureUnits = deckValue.structureUnits;
  };

  const convertToInspModuleObject = (structure) => {
    deckData.value = {
      brkey: structure.structureBridge.brkey,
      dkRatingDuration: structure?.inspectionConditionHist?.dkRatingDuration,
      dkRatingDurationChanges: structure?.inspectionConditionHist?.dkRatingDurationChanges,
      dkRating: structure?.inspection?.dkRating,
      deckWsCondRateDuration: structure?.inspectionConditionHist?.deckWsCondRateDuration,
      deckWsCondRateDurationChanges: structure?.inspectionConditionHist?.deckWsCondRateDurationChanges,
      deckWsCondRate: structure?.userInspection?.deckWsCondRate,
      dkSurfType: structure.structureBridge.dkSurfType,
      dkMembType: structure.structureBridge.dkMembType,
      dkProtect: structure.structureBridge.dkProtect,
      mainWsThickness: structure.userInspection.mainWsThickness,
      apprWsThickness: structure.userInspection.apprWsThickness,
      mainWsThickDate: structure.userInspection.mainWsThickDate,
      apprWsThickDate: structure.userInspection.apprWsThickDate,
      estChlorideContentPct: structure.userInspection.estChlorideContentPct,
      estChlorideContentDate: structure.userInspection.estChlorideContentDate,
      wearingSurfaceThickness: structure.userInspection.wearingSurfaceThickness,
      wearingSurfaceThicknessDate: structure.userInspection.wearingSurfaceThicknessDate,
      wearingSurfaceThicknessOver: structure.userInspection.wearingSurfaceThicknessOver,
      wearingSurfaceThicknessUnder: structure.userInspection.wearingSurfaceThicknessUnder,
      protectYear: structure.userBridge.protectYear,
      protectNote: structure.userBridge.protectNote,
      deckCrackingMetric: structure.userInspection.deckCrackingMetric,
      maxStructureHeight: structure.userBridge.maxStructureHeight,
      dkStrucType: structure.structureBridge.dkStrucType,
      deckWidth: structure.structureBridge.deckWidth,
      lftCurbSw: structure.structureBridge.lftCurbSw,
      rtCurbSw: structure.structureBridge.rtCurbSw,
      bridgeMed: structure.structureBridge.bridgeMed,
      roadWidth: structure?.structureBridge?.curbToCurbWidth,
      structureUnitSpanSets: structure.structureUnitSpanSets,
      structureUnitInspComments: structure.structureUnitInspComments,
      structureUnits: sortStructure(structure, "structureUnits", "strUnitLabel"),
      totalNumberOfSpans: getTotalNumberOfSpans(structure),
      nbisBridgeLength: structure.structureBridge.nbisBridgeLength,
      irregularDeckArea: structure.structureBridge.irregularDeckArea,
      deckArea: structure.structureBridge.deckArea,
      inspectionId: structure.inspection.inspectionId,
    };
    deckDataOriginal.value = clone(deckData.value);
  };

  const getInspLinkDeck = async (brkey, inspectionId) => {
    //clear idb object
    deckData.value.inspection = {};
    let isSuccess = false;

    let url = INSPECTION_LINKS_ENDPOINT.GET_DECK.replace("{brkey}", brkey).replace("{inspectionId}", inspectionId);

    await axios
      .get(url)
      .then((resp) => {
        const sortedStructures = sortStructure(resp.data.data, "structureUnits", "strUnitLabel");
        convertToInspLinkObject({ ...resp.data.data, structureUnits: sortedStructures });
        isSuccess = true;
      })
      .catch((e) => {
        LOGGER.logException(e);
      });
    return isSuccess;
  };

  const convertToInspLinkObject = (response) => {
    deckData.value = {
      brkey: response.brkey,
      inspectionId: response.inspectionId,
      dkRatingDuration: response.dkRatingDuration,
      dkRatingDurationChanges: "",
      dkRating: response.dkRating,
      deckWsCondRateDuration: response.deckWsSurfDuration,
      deckWsCondRateDurationChanges: "",
      deckWsCondRate: response.deckWsCondRate,
      dkSurfType: response.dkSurfType,
      dkMembType: response.dkMembType,
      dkProtect: response.dkProtect,
      mainWsThickness: response.mainWsThickness,
      apprWsThickness: response.apprWsThickness,
      mainWsThickDate: response.mainWsThickDate,
      apprWsThickDate: response.apprWsThickDate,
      protectYear: response.protectYear,
      protectNote: response.protectNote,
      deckCrackingMetric: response.deckCrackingMetric,
      maxStructureHeight: response.maxStructureHeight,
      dkStrucType: response.dkStrucType,
      deckWidth: response.deckWidth,
      lftCurbSw: response.lftCurbSw,
      rtCurbSw: response.rtCurbSw,
      bridgeMed: response.bridgeMed,
      inspectionComments: response.inspectionComments,
      roadWidth: response.roadWidth,
      structureUnitSpanSets: response.structureUnitSpanSets,
      structureUnitInspComments: response.structureUnitInspComments,
      structureUnits: sortStructure(response, "structureUnits", "strUnitLabel"),
      nbisBridgeLength: response.nbisBridgeLength,
      irregularDeckArea: response.irregularDeckArea,
      deckArea: response.deckArea,
      estChlorideContentPct: response.estChlorideContentPct,
      estChlorideContentDate: response.estChlorideContentDate,
      wearingSurfaceThickness: response.wearingSurfaceThickness,
      wearingSurfaceThicknessDate: response.wearingSurfaceThicknessDate,
      wearingSurfaceThicknessOver: response.wearingSurfaceThicknessOver,
      wearingSurfaceThicknessUnder: response.wearingSurfaceThicknessUnder,
      totalNumberOfSpans: response.totalNumberOfSpans,
    };
    deckDataOriginal.value = clone(deckData.value);
  };

  return {
    getStructureUnits,
    resetPageData,
    getDeckObject,
    saveDeck,
    loadDeckObject,
  };
});
